import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, DollarSign, TrendingUp } from "lucide-react";

export function BaselineMetrics() {
  const { data: activeSubscriptions } = useQuery({
    queryKey: ['active-subscriptions-count'],
    queryFn: async () => {
      const { count } = await supabase
        .from('subscriptions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');
      
      return count || 0;
    }
  });

  const { data: todayBaseline } = useQuery({
    queryKey: ['today-baseline'],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      const { data } = await supabase
        .from('cost_analytics_daily')
        .select('*')
        .eq('date', today)
        .eq('is_baseline', true)
        .maybeSingle();
      
      return data;
    }
  });

  const isZeroCostMode = activeSubscriptions === 0;

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Platform Status</h3>
        <Badge variant={isZeroCostMode ? "secondary" : "default"}>
          {isZeroCostMode ? "Zero Cost Mode" : "Active Operations"}
        </Badge>
      </div>

      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Activity className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium">Current Mode</div>
            <div className="text-xs text-muted-foreground mt-1">
              {isZeroCostMode 
                ? "Platform is operational with zero infrastructure costs. No active customers."
                : `Active operations with ${activeSubscriptions} customer${activeSubscriptions !== 1 ? 's' : ''}`
              }
            </div>
          </div>
        </div>

        {isZeroCostMode && (
          <>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-muted">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">Baseline Cost</div>
                <div className="text-xs text-muted-foreground mt-1">
                  $0.00/month - No charges until first customer
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <TrendingUp className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">First Customer Projection</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Expected costs: $2.50 (Starter), $10 (Professional), or $25 (Business)
                </div>
              </div>
            </div>
          </>
        )}

        {todayBaseline && isZeroCostMode && (
          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <div className="text-xs text-muted-foreground">
              Baseline tracked: {new Date(todayBaseline.date).toLocaleDateString()}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
